import React, { useEffect, useMemo, useState } from 'react';
import ReactFlow, { Background, Controls, MiniMap } from 'reactflow';
import 'reactflow/dist/style.css';
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
} from 'd3-force';
import { Alert, Chip, Tooltip, Typography } from '@mui/material';
import { Crosshair, Info } from 'lucide-react';
import styles from './KgGraphView.module.scss';
import { formatNumber } from './kgUtils';

// Value nodes are coloured by facet so two values from different facets don't
// read as the same kind of thing. Records stay coral — the brand colour — so the
// bipartite split is visible at a glance.
const FACET_COLORS = [
  '#8b5cf6',
  '#0ea5e9',
  '#10b981',
  '#f59e0b',
  '#ec4899',
  '#6366f1',
  '#14b8a6',
  '#f43f5e',
];

const RECORD_COLOR = '#e36250';

/**
 * ReactFlow renders records and values with fixed positions; a d3-force
 * simulation settles those positions off-canvas first. Declared at module scope
 * so ReactFlow doesn't rebuild its node registry on every render.
 */
const nodeTypes = {};

/**
 * The record graph (§7.2).
 *
 * It is BIPARTITE: record nodes connect to value nodes, never to each other. Two
 * records look related when they share a value — that is the only relationship
 * the schema actually contains. `relations[]` is stored but inert in v1, so there
 * is deliberately nothing here that implies the agent can follow record-to-record
 * edges.
 *
 * It is also always a bounded neighbourhood, never the whole dataset: a million
 * nodes cannot be laid out or read, and the browser gives out long before the
 * query does.
 */
const KgGraphView = ({ data, loading, onSelectRecord, onSelectValue, onClearFocus, focus }) => {
  const [positioned, setPositioned] = useState({ nodes: [], edges: [] });

  const facetColors = useMemo(() => {
    const map = {};
    let i = 0;
    (data?.nodes || []).forEach((n) => {
      if (n.type === 'value' && n.facet && !(n.facet in map)) {
        map[n.facet] = FACET_COLORS[i % FACET_COLORS.length];
        i += 1;
      }
    });
    return map;
  }, [data]);

  const legend = useMemo(() => {
    const seen = new Map();
    (data?.nodes || []).forEach((n) => {
      if (n.type === 'value' && n.facet && !seen.has(n.facet)) {
        seen.set(n.facet, n.facet_label || n.facet);
      }
    });
    return [...seen.entries()];
  }, [data]);

  // Run the simulation to a settled state, then hand fixed positions to
  // ReactFlow. Doing it in one pass rather than animating avoids a jittering
  // canvas and keeps ReactFlow as a pure renderer.
  useEffect(() => {
    const raw = data?.nodes || [];
    if (raw.length === 0) {
      setPositioned({ nodes: [], edges: [] });
      return;
    }

    const maxCount = Math.max(
      1,
      ...raw.filter((n) => n.type === 'value').map((n) => Number(n.record_count || 1))
    );

    const simNodes = raw.map((n) => ({ ...n }));
    const byId = Object.fromEntries(simNodes.map((n) => [n.id, n]));
    const simLinks = (data.edges || [])
      .filter((e) => byId[e.source] && byId[e.target])
      .map((e) => ({ source: e.source, target: e.target }));

    // Value nodes are heavier and repel harder, so they spread out and pull
    // their records into readable clusters — clustering by shared value IS the
    // signal this view exists to show.
    const radiusOf = (n) =>
      n.type === 'value'
        ? 12 + 22 * Math.sqrt(Number(n.record_count || 1) / maxCount)
        : 7;

    const simulation = forceSimulation(simNodes)
      .force('link', forceLink(simLinks).id((d) => d.id).distance(80).strength(0.35))
      .force('charge', forceManyBody().strength((d) => (d.type === 'value' ? -420 : -110)))
      .force('center', forceCenter(0, 0))
      .force('collide', forceCollide().radius((d) => radiusOf(d) + 10))
      .stop();

    // A fixed tick count keeps this deterministic and bounded — at most 200
    // nodes, so it settles in a few milliseconds.
    for (let i = 0; i < 300; i += 1) simulation.tick();

    const nodes = simNodes.map((n) => {
      const isRecord = n.type === 'record';
      const size = radiusOf(n) * 2;
      const color = isRecord ? RECORD_COLOR : facetColors[n.facet] || '#6366f1';
      const label =
        n.label && n.label.length > 34 ? `${n.label.slice(0, 34)}…` : n.label || n.id;
      return {
        id: n.id,
        type: 'default',
        position: { x: n.x, y: n.y },
        data: {
          label: isRecord ? label : `${label} · ${formatNumber(n.record_count || 0)}`,
          raw: n,
        },
        style: {
          background: isRecord ? 'transparent' : `${color}1f`,
          border: `2px solid ${color}`,
          borderRadius: isRecord ? '8px' : '999px',
          color: 'var(--text-primary)',
          fontSize: isRecord ? 10 : Math.max(10, Math.min(14, size / 4)),
          fontWeight: isRecord ? 500 : 700,
          padding: isRecord ? '5px 9px' : '7px 13px',
          maxWidth: 210,
          textAlign: 'center',
          cursor: 'pointer',
        },
      };
    });

    const edges = (data.edges || []).map((e, i) => ({
      id: `e-${i}-${e.source}-${e.target}`,
      source: e.source,
      target: e.target,
      type: 'straight',
      style: { stroke: 'var(--border-color)', strokeWidth: 1 },
    }));

    setPositioned({ nodes, edges });
  }, [data, facetColors]);

  const handleNodeClick = (_, node) => {
    const raw = node?.data?.raw;
    if (!raw) return;
    // A record opens its detail; a value becomes a filter. That is what makes
    // this a navigation surface rather than a picture.
    if (raw.type === 'record') onSelectRecord(raw.record_key);
    else onSelectValue(raw.facet, raw.label);
  };

  if (loading) {
    return <div className={styles.placeholder}>Building the graph…</div>;
  }

  if (!data) return null;

  const truncated = Boolean(data.truncated);
  const excluded = Number(data.excluded_common_values || 0);

  return (
    <div className={styles.wrap}>
      {/* Everything below is rendered, not merely received. A graph quietly
          drawing 100 of 40,000 records reads as the whole dataset. */}
      <div className={styles.notices}>
        {truncated && (
          <Alert severity="info" className={styles.notice}>
            <strong>
              Showing {formatNumber(data.shown_records)} of{' '}
              {formatNumber(data.cohort_size)} records.
            </strong>{' '}
            This is a sample, not the whole set. Narrow the filters, or open a record and choose
            “Show what this connects to” to explore outward from it.
          </Alert>
        )}
        {excluded > 0 && (
          <Alert severity="info" className={styles.notice}>
            <strong>
              {excluded} very common value{excluded === 1 ? '' : 's'} hidden.
            </strong>{' '}
            Values appearing on more than {data.common_threshold_pct}% of records connect almost
            every node to every other one, which collapses the layout into a hairball. Sparse
            here doesn’t mean sparse data.
          </Alert>
        )}
        {data.message && (
          <Alert severity="warning" className={styles.notice}>
            {data.message}
          </Alert>
        )}
      </div>

      {focus && (
        <div className={styles.focusBar}>
          <Crosshair size={14} />
          <span>
            Centred on <code>{focus}</code>
          </span>
          <Chip size="small" label="Show everything" onClick={onClearFocus} variant="outlined" />
        </div>
      )}

      {positioned.nodes.length === 0 ? (
        <div className={styles.placeholder}>
          Nothing to draw for this filter.
        </div>
      ) : (
        <div className={styles.canvas}>
          <ReactFlow
            nodes={positioned.nodes}
            edges={positioned.edges}
            nodeTypes={nodeTypes}
            onNodeClick={handleNodeClick}
            fitView
            minZoom={0.1}
            maxZoom={2.5}
            nodesDraggable
            nodesConnectable={false}
            elementsSelectable
            proOptions={{ hideAttribution: false }}
          >
            <Controls showInteractive={false} />
            <MiniMap
              pannable
              zoomable
              nodeColor={(n) =>
                n.data?.raw?.type === 'record'
                  ? RECORD_COLOR
                  : facetColors[n.data?.raw?.facet] || '#6366f1'
              }
            />
            <Background variant="dots" gap={16} size={1} />
          </ReactFlow>
        </div>
      )}

      {/* Legend — records and values are different kinds of thing, and two
          values from different facets carry different meaning. */}
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.swatchRecord} /> Record
        </span>
        {legend.map(([facet, label]) => (
          <span key={facet} className={styles.legendItem}>
            <span
              className={styles.swatchValue}
              style={{ borderColor: facetColors[facet], background: `${facetColors[facet]}1f` }}
            />
            {label}
          </span>
        ))}
        <Tooltip title="Value circles are sized by how many records carry them.">
          <span className={styles.legendHint}>
            <Info size={12} /> size = record count
          </span>
        </Tooltip>
      </div>

      <Typography variant="caption" className={styles.footnote}>
        Records connect to the values they carry, never directly to each other — two records
        appear related when they share a value. Click a record to open it, or a value to filter
        by it.
      </Typography>
    </div>
  );
};

export default KgGraphView;
